
// Service worker updates implemented following:
// https://medium.com/@dougallrich/give-users-control-over-app-updates-in-vue-cli-3-pwas-20453aedc1f2

import { Component, Vue, Watch } from "vue-property-decorator"
import * as cdn from "@coredination/components"
import eventbus from "./eventbus"
import Login from "./components/Login.vue"
import NavbarOptions, { navbar } from "./components/NavbarOptions.vue"
import CdnEventNotifications from "./components/CdnEventNotifications.vue"
import EntityEventSubscription from "@/graphql/subscription/entityevent.graphql"
import { useMutation, useQuery, useApolloClient } from '@vue/apollo-composable'

import InvalidateToken from "./graphql/auth/invalidateToken.graphql"
import ValidateToken from "./graphql/auth/validateToken.graphql"
import { BeforeInstallPromptEvent } from "vue-pwa-install"
import { RawLocation, Route } from "vue-router"
import { User } from "@coredination/common"

import { messaging, vapidKey } from "@/plugins/firebase"
import { getToken, onMessage } from "firebase/messaging"
import gql from 'graphql-tag'

import CountUnreadMessages from "./graphql/messages/countUnreadMessages.graphql"

@Component({
    components: {
        Login,
        NavbarOptions,
        CdnEventNotifications
    },
    apollo: {
        me: {
            ...cdn.graphql.me,
            skip() {
                return !this.authenticated
            },
        },
        countUnreadMessages: {
            query: CountUnreadMessages,
            pollInterval: 30000,
            skip() {
                return !this.authenticated
            },
        },
        $subscribe: {
            entityEvent: {
                query: EntityEventSubscription,
                result(result:any) {
                    const event = result?.data?.entityEvent
                    var cache : any = this.$apollo?.provider?.defaultClient?.cache
                    const compositionApolloClient = useApolloClient()
                    var compositionCache: any = compositionApolloClient.client.cache
                    this.invalidateCacheOnEvent(cache, event)
                    this.invalidateCacheOnEvent(compositionCache, event, true) // invalidate new composition cache as well
                },
            },
        },
    },
})
export default class App extends Vue {
    readonly mobileBreakPoint = 960
    readonly avatarFallbackUrl = "//secure.gravatar.com/avatar/0?d=mm&s=120"

    // Desktop navigation drawer menu items
    navItems: any[] = [
        { to: "/", icon: "home", title: this.$t("nav_home") },
        { to: "/planning", icon: "event", title: this.$t("nav_planning") },
        { to: "/history", icon: "event_repeat", title: this.$t("nav_history") },
        { to: "/search", icon: "search", title: this.$t("nav_search") },
        {
            to: "/map",
            icon: "map",
            title: this.$t("nav_map"),
            //if: () => this.me && this.me.hasPermission("view_map"),
        },
        { to: "/users", icon: "person", title: this.$t("users") },
        //{ to: "/directory", icon: "list_alt", title: this.$t("nav_directory") },
        { to: "/settings", icon: "settings", title: this.$t("nav_settings") },
        // { to: "/uxx", icon: "lightbulb", title: "UX Experiments", if: () => this.me && this.me.hasPermission("testing"), },
        // { to: "/about", icon: "info", title: this.$t("nav_about") },
        {
            to: null,
            icon: "cloud_download",
            title: this.$t("installApp"),
            if: () => this.showInstall,
            click: this.install,
        },
    ]

    tab = ""
    tabNavStack: { [key: string]: string[] } = {}
    tabNavigationDisabled = false
    delayRenderingView = false
    drawer = true
    desktop = true

    avatarImageFailed: boolean = false
    authenticated: boolean = cdn.apollo.isAuthenticated() || false
    me?: User

    showInstall = false
    showInstallBanner = false
    showIosInstallBanner = false
    dismissedInstall = false
    deferredPrompt: BeforeInstallPromptEvent | null = null

    refreshing = false
    registration: any = null
    updateExists = false
    header: any = this.appName
    unsavedChanges = false
    pullRefreshOption = false

    countUnreadMessages?:any

    get inhibitPullToRefresh() {
        return localStorage && !!localStorage.getItem("inhibitPullToRefresh")
    }

    get ios() {
        const userAgent = window.navigator.userAgent.toLowerCase()
        return this.iosWrapperClient || /iphone|ipad|ipod/.test(userAgent)
    }

    get iosWrapperClient() {
        // ios wrapper app uses a custom user agent
        const userAgent = window.navigator.userAgent.toLowerCase()
        return userAgent == "cdn-ios-wrapper-app"
    }

    get androidWrapperClient() {
        const userAgent = window.navigator.userAgent.toLowerCase()
        return userAgent == "cdn-android-wrapper-app"
    }

    get isWrapperClient() {
        return this.iosWrapperClient || this.androidWrapperClient
    }

    get tabItems() {
        var tabItems: any[] = [
            {
                name: "home",
                path: "/",
                icon: "home",
            },
            {
                name: "planning",
                path: "/planning",
                icon: "event",
            },
            {
                name: "history",
                path: "/history",
                icon: "event_repeat",
            },
            {
                name: "search",
                path: "/search",
                icon: "search",
            },
            {
                name: "menu",
                path: "/menu",
                icon: "menu",
            }
        ]
        return tabItems
    }

    get standalone() {
        if (window.matchMedia("(display-mode: standalone)").matches) return true
        const navigateur = window.navigator as any
        return "standalone" in navigateur && navigateur.standalone
    }

    get noAuthRequired() {
        return this.$route.meta && this.$route.meta.noAuthRequired
    }
    get avatarUrl() {
        return (!this.avatarImageFailed && this.me && this.me.avatarUrl) || this.avatarFallbackUrl
    }

    get appName() {
        return process!.env!.VUE_APP_NAME
    }

    get version() {
        try {
            return require("../package.json").version
        } catch (error) {
            console.error("couldn't load package version", error)
        }
    }

    get currentTabItem() {
        return this.tabItems.find((tabItem) => tabItem.name == this.tab)
    }

    get currentTabNavStack() {
        if (!(this.tab in this.tabNavStack)) this.tabNavStack[this.tab] = []
        return this.tabNavStack[this.tab]
    }

    clickTabItem(tabItem: any) {
        if (this.tab == tabItem.name) {
            if (this.currentTabNavStack.length > 0) {
                //console.log(`go to ${this.tab} root`)
                this.currentTabNavStack.splice(0)
                this.$router.push(tabItem.path)
            } else if (location.pathname != tabItem.path) {
                //console.log(`go to ${this.tab} root with no stack`)
                this.$router.push(tabItem.path)
            } else if (tabItem.name === 'search') {
                eventbus.$emit('search-tab-clicked');
            }
        } else {
            //console.log("switch tab from", this.tab, "to", tabItem.name)
            this.tab = tabItem.name
            if (this.currentTabNavStack.length > 0) {
                //console.log("from stack", this.currentTabNavStack.length)
                this.$router.push(this.currentTabNavStack[this.currentTabNavStack.length - 1])
            } else {
                //console.log("root")
                this.$router.push(tabItem.path)
            }
        }
    }

    clickTabNavBack() {
        const last = this.currentTabNavStack.pop()
        if (this.currentTabNavStack.length > 0) {
            this.$router.push(this.currentTabNavStack[this.currentTabNavStack.length - 1]).catch((reason: Error) => {
                if (!reason.message || !reason.message.startsWith("Navigation aborted")) console.error(reason)
                if (last) this.currentTabNavStack.push(last)
            })
        } else if (this.currentTabItem && this.currentTabItem.path) {
            this.$router.push(this.currentTabItem.path).catch((reason: Error) => {
                if (!reason.message || !reason.message.startsWith("Navigation aborted")) console.error(reason)
                if (last) this.currentTabNavStack.push(last)
            })
        }
    }

    @Watch("$route", { immediate: true, deep: true })
    onUrlChange(to: Route, from: Route, other: any) {
        // Reset stuff that views emit-enable
        this.tabNavigationDisabled = false
        this.unsavedChanges = false
        this.pullRefreshOption = false
        navbar?.clearOptions()

        if ((this.showInstallBanner || this.showIosInstallBanner) && !["/", "/about"].includes(to.path)) {
            this.showInstallBanner = this.showIosInstallBanner = false
        }
        if (this.desktop) {
            // skip all the tab nav stack stuff in desktop/drawer mode
            this.tabNavStack = {}
        } else {
            // restore tab from browser history state when using the back button
            if (history.state && "tab" in history.state) {
                this.tab = history.state.tab
            }
            // pop from tab nav stack when using the back button
            if (
                from &&
                this.currentTabNavStack.length > 1 &&
                this.currentTabNavStack[this.currentTabNavStack.length - 1] == from.fullPath &&
                this.currentTabNavStack[this.currentTabNavStack.length - 2] == to.fullPath
            ) {
                //console.log("back pop stack")
                this.currentTabNavStack.pop()
            }
            // does this navigation lead to a tab root?
            let tabRoot = ""
            for (var tabItem of this.tabItems) {
                if (to.fullPath == tabItem.path) {
                    tabRoot = tabItem.name
                    break
                }
            }
            if (!tabRoot) {
                // push to tab nav stack, unless it's already there
                if (
                    this.currentTabNavStack.length == 0 ||
                    this.currentTabNavStack[this.currentTabNavStack.length - 1] != to.fullPath
                ) {
                    //console.log("push", this.tab, this.currentTabNavStack.length, to.fullPath)
                    this.currentTabNavStack.push(to.fullPath)
                } else {
                    //console.log("top of stack", this.tab, this.currentTabNavStack.length, to.fullPath)
                }
            } else {
                if (tabRoot != this.tab) {
                    //console.log("switching to", tabRoot)
                    this.tab = tabRoot
                } else {
                    //console.log("tab root", tabRoot)
                }
                this.currentTabNavStack.splice(0)
            }
            // store tab in browser history state, so that we can change tab when using the back button
            history.replaceState({ tab: this.tab }, "")
            // delay rendering so that tab change is instant even if the view is heavy
            this.delayRenderingView = true
            setTimeout(() => { this.delayRenderingView = false })
        }

        this.updateHeader(to.path)
    }

    // Use this instead of this.$router.replace in order to make tab navigation work...
    replaceRoute(to: RawLocation) {
        //console.log("replace route")
        this.currentTabNavStack.pop()
        this.$router.replace(to)
    }

    disableTabNavigation(disable = true) {
        this.tabNavigationDisabled = !!disable
    }

    setUnsavedChanges(value = true) {
        this.unsavedChanges = !!value
    }

    setPullRefreshOption(value = true) {
        this.pullRefreshOption = !!value
    }

    @Watch("authenticated")
    authenticatedChanged(authenticated: boolean) {
        this.updateHeader(this.$route.path)
    }

    updateHeader(routePath: any) {
        if (routePath.includes("absence")) this.header = this.$t("absence")
        else if (routePath.includes("workreport")) this.header = this.$t("timeReport")
        else if (routePath.includes("job")) this.header = this.$t("job")
        else if (routePath.includes("asset")) this.header = this.$t("asset")
        else if (routePath.includes("customer/project")) this.header = this.$t("customerProject")
        else if (routePath.includes("customer")) this.header = this.$t("customer")
        else if (routePath.includes("notifications")) this.header = this.$t("notifications")
        else if (routePath.includes("untimed")) this.header = this.$t("untimedJobs")
        else if (routePath.includes("late")) this.header = this.$t("lateJobs")
        else if (routePath.includes("remotesignature")) this.header = this.$t("remoteSignature")
        else if (routePath.includes("users")) this.header = this.$t("users")
        else {
            var rootPath = "/" + routePath.split("/")[1]
            var currentPage = this.navItems.filter((elem) => elem.to === rootPath)
            this.header = currentPage.length >= 1 && this.authenticated ? currentPage[0].title : this.appName
        }
    }

    onLoggedIn() {
        this.authenticated = cdn.apollo.isAuthenticated()
        if (this.authenticated) this.autoInitNotifications()
    }
    logout() {
        let apolloToken: string | null = localStorage.getItem("apollo-token")
        this.$apollo.mutate({ mutation: InvalidateToken })
        cdn.apollo.onLogout(this.$apollo.provider.defaultClient)
        const compositionApolloClient = useApolloClient()
        cdn.apollo.onLogout(compositionApolloClient.client) // also clear the new composition client
        this.authenticated = cdn.apollo.isAuthenticated()
        this.me = undefined
        // @ts-ignore: The line below calls a method in Android which unregisters the wrapper for push. The Android method is not visible to Vue, so the lint is ignored.
        if (this.androidWrapperClient) Android.onLoggedOut(apolloToken);
        if (this.iosWrapperClient) {
            try {
                (window as any).webkit.messageHandlers.toggleMessageHandler.postMessage({
                    message: "logged-out",
                    "apollo-token": apolloToken
                })
            } catch (e) {
                console.error('could not access window.webkit to pass logged-out message to ios wrapper')
            }
        }
        if (sessionStorage.clear) sessionStorage.clear()
        if (localStorage.clear) {
            var email = localStorage.getItem('email')
            localStorage.clear();
            if (email) localStorage.setItem('email', email);
        }
    }
    avatarImageError() {
        this.avatarImageFailed = true
    }

    install() {
        this.showInstallBanner = false

        if (location.protocol == "http:" && location.hostname == "localhost") {
            alert("Ain't gonna work on localhost")
            return
        }
        // Show the prompt:
        this.deferredPrompt!.prompt()

        // Wait for the user to respond to the prompt:
        this.deferredPrompt!.userChoice.then((choiceResult) => {
            if (choiceResult.outcome === "accepted") {
                console.log("User accepted the install prompt")
                this.showInstall = false
                localStorage.removeItem("dismissedInstall")
            } else {
                console.log("User dismissed the install prompt")
            }

            this.deferredPrompt = null
        })
    }

    dismissInstall() {
        localStorage.setItem("dismissedInstall", "sadly")
        this.dismissedInstall = true
        this.showIosInstallBanner = false
        setTimeout(() => {
            this.showInstallBanner = false
        }, 3000)
    }
    created() {
        var self = this
        // $appHeader is a global getter/setter that can be used to set the header from anywhere in the app, using Vue.set(Vue.prototype, '$appHeader',  "insert text"), or get using this.$header
        if (Vue.prototype.$appHeader === undefined)
            Object.defineProperty(Vue.prototype, "$appHeader", {
                get() {
                    return self.header
                },
                set(value) {
                    self.header = value
                },
            })
        this.$on("canInstall", (event: BeforeInstallPromptEvent) => {
            // Prevent Chrome 67 and earlier from automatically showing the prompt:
            event.preventDefault()
            // Stash the event so it can be triggered later:
            this.deferredPrompt = event
            this.showInstall = true
            this.showInstallBanner = !this.isWrapperClient ? !localStorage.getItem("dismissedInstall") : false
        })

        document.addEventListener("swUpdated", this.showRefreshUI, { once: true })
        if (!this.iosWrapperClient) {
            // service worker does not exist in wrapper app browser, so this crashes,
            // resulting in none of the emit handlers getting attached
            navigator.serviceWorker.addEventListener("controllerchange", () => {
                console.log("controllerchange")
                if (!this.authenticated) {
                    console.log("not logged in, skipping refresh")
                    return
                }
                if (this.refreshing) {
                    console.error("already refreshing - so let's not")
                    return
                }
                console.log("refreshing")
                this.refreshing = true
                window.location.reload()
            })
        }

        if (!this.isWrapperClient) {
            if (this.ios && !this.standalone && !localStorage.getItem("dismissedInstall")) {
                this.showIosInstallBanner = true
            } else if (this.ios && this.standalone) {
                localStorage.setItem("dismissedInstall", "alreadyInstalled")
            }
        }

        this.$root.$on("install", this.install)
        this.$root.$on("logout", this.logout)
        this.$root.$on("initNotifications", this.initNotifications)
        this.$root.$on("replaceRoute", this.replaceRoute)
        this.$root.$on("disableTabNavigation", this.disableTabNavigation)
        this.$root.$on("setUnsavedChanges", this.setUnsavedChanges)
        this.$root.$on("setPullRefreshOption", this.setPullRefreshOption)
        Vue.prototype.$iosWrapperClient = this.iosWrapperClient
    }

    beforeMount() {
        if (!this.isWrapperClient)
            return
        (window as { [key: string]: any })['rerouteFromNativeNotification'] = this.rerouteFromNativeNotification
    }

    refreshCurrentView(e: any) {
        this.$root.$emit("pull-refresh-view")
    }

    showRefreshUI(e: any) {
        this.registration = e.detail
        this.updateExists = true
    }

    dismissUpdate() {
        this.updateExists = false
    }

    refreshApp() {
        this.updateExists = false
        if (!this.registration) {
            console.error("Can't refresh - No registration")
            return
        }
        if (!this.registration.waiting) {
            console.error("Can't refresh - Registration is not waiting")
            return
        }
        console.log("Skip waiting")
        this.registration.waiting.postMessage("skipWaiting")
    }

    mounted() {
        if (!this.desktop && history.state && "tab" in history.state) {
            this.tab = history.state.tab
        }
        if (this.currentTabItem && location.pathname != this.currentTabItem.path) {
            //console.log(`tab navigation starts with one item in stack`)
            this.currentTabNavStack.push(location.pathname)
        }
        setTimeout(() => {
            this.resize()
        }, 0)
        eventbus.$on("graphql-error", (error: any) => {
            let message = error.message
            if (error.graphQLErrors) {
                let messages = []
                for (const gerror of error.graphQLErrors) {
                    if (messages.indexOf(gerror.message) < 0) messages.push(gerror.message)
                }
                message = messages.join(", ")
            }
            if (!message && error.toString) message = error.toString()
            ;(this as any).$toast.error(message, {
                icon: "warning",
            })
        })
        this.avatarImageFailed = false
        if (this.authenticated) {
            console.log("Validate authentication")
            this.$apollo.query({ query: ValidateToken, variables: { appId: process.env.VUE_APP_FRONTEND_URL },}).catch((error) => {
                console.warn("Token validation failed - unauthenticated", error.graphQLErrors)
                for (const gerror of error.graphQLErrors) {
                    if (gerror.extensions && gerror.extensions.code === "UNAUTHENTICATED") {
                        cdn.apollo.onLogout(this.$apollo.provider.defaultClient)
                        this.authenticated = false
                        return
                    }
                }
            })
            this.autoInitNotifications()
        }
        console.log("app thinks navbar is", navbar)
    }

    destroyed() {
        this.$root.$off("replaceRoute")
    }

    hideBottomNav = false;
    currentHeight: any

    resize() {
        const desktop = window.innerWidth >= this.mobileBreakPoint
        if (desktop && !this.desktop) {
            console.log("Switching to desktop mode")
            this.drawer = true
        } else if (!desktop && this.desktop) {
            console.log("Switching to mobile mode")
        }
        if (!desktop && this.drawer) this.drawer = false
        this.desktop = desktop

        // #16351 - Some hacks to hide the bottom navigation when Android keyboard is shown
        this.hideBottomNav = this.androidWrapperClient
            && window.innerWidth * 1.5 > window.innerHeight
            && this.currentHeight && this.currentHeight - window.innerHeight > 200
        this.currentHeight = window.innerHeight
    }

    miniDrawer = false
    handleDrawerInput(isOpen: boolean) {
        this.miniDrawer = !isOpen
    }

    autoInitNotifications() {
        // TODO this will be called when authenticated... but we need to make it opt-in - it's kinda intrusive
        //this.initNotifications()
        
        // @ts-ignore: The line below calls a method in Android which registers the wrapper for push. The Android method is not visible to Vue, so the lint is ignored.
        if (this.androidWrapperClient) Android.onLoggedIn()
        if (this.iosWrapperClient) {
            try {
                (window as any).webkit.messageHandlers.toggleMessageHandler.postMessage({
                    message: "logged-in",
                    "apollo-token": localStorage.getItem("apollo-token")
                })
            } catch (e) {
                console.error('could not access window.webkit to pass logged-in message to ios wrapper')
            }
        }
    }

    initNotifications() {
        // Some complexity here due to Safari/iOS fuckery
        // See https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API/Using_the_Notifications_API

        if (!messaging) return
        const handlePermission = (permission: NotificationPermission) => {
            console.log("Notification permission", permission)
            sessionStorage.notificationPermission = "" + permission
            this.$root.$emit("notificationPermission", sessionStorage.notificationPermission)
            if (permission === "granted" && !this.androidWrapperClient && !this.iosWrapperClient) {
                getToken(messaging!, { vapidKey })
                    .then((currentToken) => {
                        if (currentToken) {
                            console.log("FCM token", currentToken)
                            sessionStorage.fcmToken = currentToken
                            this.$root.$emit("fcmToken", currentToken)
                            onMessage(messaging!, (payload) => {
                                console.log("Message received: ", payload)
                                if (payload.notification && payload.notification.title) {
                                    new Notification(payload.notification.title, payload.notification)
                                }
                                // ...
                            })
                            this.$apollo
                                .mutate({
                                    mutation: gql`mutation RegisterForFirebase($token: String!) {
                                        registerForFirebase(token: $token)
                                    }`,
                                    variables: {
                                        token: currentToken,
                                    },
                                })
                            // Send the token to your server and update the UI if necessary
                            // ...
                            sessionStorage.fcmTokenResult = "OK"
                            this.$root.$emit("fcmTokenResult", sessionStorage.fcmTokenResult)
                        } else {
                            // Show permission request UI
                            console.warn("No registration token available. Request permission to generate one.")
                            sessionStorage.fcmTokenResult = "No registration token"
                            this.$root.$emit("fcmTokenResult", sessionStorage.fcmTokenResult)
                        }
                    })
                    .catch((err) => {
                        console.error("An error occurred while retrieving token. ", err)
                        sessionStorage.fcmTokenResult = "Error" + err
                        this.$root.$emit("fcmTokenResult", sessionStorage.fcmTokenResult)
                    })
            }
        }

        const checkNotificationPromise = () => {
            try {
                Notification.requestPermission().then()
            } catch (e) {
                return false
            }
            return true
        }

        if (!("Notification" in window)) {
            console.log("This browser does not support notifications.")
            sessionStorage.notificationPermission = "no support"
            this.$root.$emit("notificationPermission", sessionStorage.notificationPermission)
        } else {
            if (checkNotificationPromise()) {
                sessionStorage.notificationPermission = "promise"
                this.$root.$emit("notificationPermission", sessionStorage.notificationPermission)
                Notification.requestPermission().then(handlePermission)
            } else {
                sessionStorage.notificationPermission = "no promise"
                this.$root.$emit("notificationPermission", sessionStorage.notificationPermission)
                Notification.requestPermission(handlePermission)
            }
        }
    }

    rerouteFromNativeNotification(notificationInfo: any) {
        // used by wrapper apps to get access to a function which can push entries on to router
        var to: string = 'notifications'
        try {
            notificationInfo = JSON.parse(notificationInfo);
            to = notificationInfo.path as string
        } catch (e) {
            console.error('could not reroute from native notification', e)
        }
        this.$router.push(to)
        return true
    }

    invalidateCacheOnEvent(cache: any, event: any, composition: any) {
        const uuid = event?.data?.uuid
        var entityClass = event?.data?.entityClass
        if (entityClass == "Form") entityClass = "CustomForm"

        if (event.type == "groupMembershipsChanged" && composition)
            cache.evict({ fieldName: 'users' });

        if (event?.type && uuid && entityClass && cache?.data?.data) {
            console.log('ws event received, invalidating cache.', event.type, event)
            var currentEntity = cache.data.data[entityClass + ':' + uuid]
            if (entityClass == "Job" && !currentEntity) currentEntity = cache.data.data['JobListItem:' + uuid]
            if (event.type == "entityCreated" && entityClass == "WorkReport"){
                Object.keys(cache.data.data).forEach(key => {
                    if (key.match(/(WorkReport:)/)) {
                        cache.data.delete(key)
                    }
                })
            }
            if (entityClass == "Job" && !currentEntity && event?.data?.jobResourceUserIds && this.me?.id && event.data.jobResourceUserIds.includes(parseInt(this.me.id))){
                Object.keys(cache.data.data).forEach(key => {
                    if (key.match(/(myWorkStats|myCurrentJob|JobListItem)/)) {
                        cache.data.delete(key)
                    }
                })
            }
            //const currentlyViewingEntity = type && (type == "Job" || type == "WorkReport") && this.$route.path.indexOf('/' + type.toLowerCase() + '/' + uuid) >= 0
            const alreadyUpdated = currentEntity?.version && event?.data?.version && currentEntity.version >= event.data.version
            if (alreadyUpdated) console.log('already updated, skipping')
            Object.keys(cache.data.data).forEach((key) => {
                if (key.indexOf('EntityEventData') >= 0 || key.indexOf('event') >= 0) return
                if (key.indexOf('User:') >= 0 && event?.type != "groupMembershipsChanged") return
                if (entityClass == "Customer" && key.indexOf('CustomerProject') >= 0) {
                    const customerProject = cache.data.data[key]
                    if (customerProject?.customerUuid && customerProject.customerUuid == uuid){
                        cache.data.delete(key)
                    }
                    return
                }
                if (key.indexOf(uuid) >= 0 && !(alreadyUpdated && key == entityClass + ':' + uuid)){
                    cache.data.delete(key)
                }
            })
        }
    }
}
